/**
 * Stable error fingerprint for **client-side per-session send suppression**.
 *
 * Moved from `@plerity/runtime-reporter` (which re-exports `computeFingerprint`
 * under the same name). Golden vectors live in
 * `fixtures/fingerprint-vectors.json` so the web and any other implementation
 * can be pinned to the same output.
 *
 * Algorithm: sha256(message[:200] + "|" + firstStackFrame[:200] + "|" + category)[:32]
 *
 * Relationship to the server fingerprint (corrected 2026-08-03)
 * -------------------------------------------------------------
 * This file used to claim it "MUST match the server-side fingerprint
 * algorithm". That was never true and must not be made true:
 *
 * 1. The value is never transmitted. The ingest schema
 *    (`ErrorReport` in `backend/servers/_shared/observability_router.py`) has
 *    no `fingerprint` field; the server always computes its own from the
 *    payload. The two are never compared.
 * 2. They serve opposite purposes. Here the fingerprint decides *"have we
 *    already beaconed this error in this session?"*. Server-side it is the
 *    `runtime_errors` storage/UPSERT key.
 * 3. Because of (2), the server normalises volatile entity ids out of the
 *    message before hashing and this MUST NOT. One page load that fires 28
 *    real 401s against 28 different task ids should send 28 beacons so the
 *    server records `occurrence_count = 28`. Normalising here would suppress
 *    27 of them and the sink would record 1 — the exact under-count the
 *    server-side fix exists to remove.
 *
 * Keep this hash raw. See `_compute_fingerprint`'s docstring for the
 * server-side rationale and the measured numbers.
 *
 * Uses the WebCrypto API (available in modern browsers + React Native via
 * react-native-quick-crypto polyfill). Falls back to a fast string-based
 * hash if crypto.subtle is unavailable.
 */

import type { ErrorCategory } from "./types";

/** Extract the first non-header line from a stack trace. */
function firstStackFrame(stack: string | undefined): string {
  if (!stack) return "";
  for (const line of stack.split("\n")) {
    const stripped = line.trim();
    if (stripped && !stripped.startsWith("Error")) {
      return stripped.slice(0, 200);
    }
  }
  return "";
}

/** Cheap non-cryptographic 32-bit hash (FNV-1a variant). Fallback only. */
function fallbackHash(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0").repeat(4); // 32 hex chars
}

/** The exact string that is hashed. Exported so vectors can pin it. */
export function fingerprintPayload(message: string, stack: string | undefined, category: ErrorCategory): string {
  return `${message.slice(0, 200)}|${firstStackFrame(stack)}|${category}`;
}

/** The non-cryptographic fallback used when `crypto.subtle` is unavailable. */
export function fallbackFingerprint(message: string, stack: string | undefined, category: ErrorCategory): string {
  return fallbackHash(fingerprintPayload(message, stack, category));
}

export async function computeFingerprint(
  message: string,
  stack: string | undefined,
  category: ErrorCategory
): Promise<string> {
  const payload = fingerprintPayload(message, stack, category);
  // Prefer WebCrypto sha256 for parity with the server.
  if (typeof crypto !== "undefined" && crypto.subtle) {
    try {
      const buf = new TextEncoder().encode(payload);
      const digest = await crypto.subtle.digest("SHA-256", buf);
      const hex = Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      return hex.slice(0, 32);
    } catch {
      // fall through
    }
  }
  return fallbackHash(payload);
}
